/**
 * CommunityMessageAuthorInfo — the avatar + author-name + timestamp row shown
 * below a community message bubble (and below the reply-preview bubble).
 *
 * Flutter source of truth: `userInfo` (`community_message_item.dart:176-231`)
 * and `messageTimestamp` (`:233-236`).
 *
 * Behaviour (matches Flutter line-for-line):
 *  - Avatar from `UserUtils.generateUserProfileImageUrl(authorUserId)`
 *    (Flutter `TextUtils.generateUserProfileImageUrl`, `:189-196`).
 *  - Name shows the `"You"` l10n label when the author IS the current user,
 *    else the author's `userName` (`:184-194`).
 *  - Timestamp is the formatted main-message time (`:225`, `:233-236`).
 *  - Tapping the row navigates to `tapUserId` — which Flutter ALWAYS sets to the
 *    MAIN message author's `messageUsersInfo.userId`, even inside the reply
 *    preview (`:201`). The caller passes the correct id; this component does not
 *    re-derive it.
 *
 * Reused by both `CommunityMessageItem` (main author) and
 * `CommunityMessageReplyPreview` (replied author) to keep the item small
 * (`presentation.md` § Component size).
 *
 * Theme / responsive tokens:
 *  - Avatar size → `responsive.avatar.xs` (= 24; Flutter
 *    `videoUserProfileSize = 26` — closest token, small UI deviation).
 *  - Name color/size → `theme.white` / `responsive.font.sm` (Flutter
 *    `communityUserNameStyle`: white, 12 — `app_styles.dart:828-834`).
 *  - Timestamp color/size → `theme.text.secondary` / `responsive.font.xs`
 *    (Flutter `communityTimeStyle`: `textColor`, 10 — `app_styles.dart:836-842`).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface CommunityMessageAuthorInfoProps {
  /** Author whose avatar/name are shown (main author or replied author). */
  authorUserId: string;
  /** Author display name (ignored when the author is the current user). */
  authorUserName: string;
  /** Current user's Firebase id — drives the `"You"` label. */
  currentUserId: string;
  /**
   * The id navigated to on tap. Flutter always navigates to the MAIN message
   * author (`community_message_item.dart:201`), even from the reply preview, so
   * the caller passes that id rather than `authorUserId`.
   */
  tapUserId: string;
  /** Optional preformatted timestamp string (main message time). Omitted on the reply preview. */
  timestampLabel?: string;
}

export const CommunityMessageAuthorInfo: React.FC<CommunityMessageAuthorInfoProps> = ({
  authorUserId,
  authorUserName,
  currentUserId,
  tapUserId,
  timestampLabel,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const isOwner = authorUserId === currentUserId;
  const name = isOwner ? l10n.community.you : authorUserName;

  const handleTap = (): void => {
    Navigator.navigateToUserDetails(tapUserId);
  };
  const handleKeyDown = useEnterOrSpaceActivation<HTMLDivElement>(handleTap);

  const avatarSize = responsive.avatar.xs;

  return (
    <div
      {...qaAttr({ id: `community-message-author-${authorUserId}` })}
      role="button"
      tabIndex={0}
      onClick={handleTap}
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: responsive.spacing.xs,
        cursor: 'pointer',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(authorUserId)}
        alt=""
        style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span
          style={{
            color: theme.white,
            fontSize: responsive.font.sm,
            fontWeight: responsive.fontWeight.regular,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </span>
        {timestampLabel !== undefined && (
          <span
            style={{
              color: theme.text.secondary,
              fontSize: responsive.font.xs,
              fontWeight: responsive.fontWeight.regular,
            }}
          >
            {timestampLabel}
          </span>
        )}
      </div>
    </div>
  );
};
